import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  decomActions,
  isVaultExportAction,
} from '../../../../../shared/decom-actions';
import {
  ActionErrorEntry,
  createDefaultActionData,
} from '../decom-policy-utils.tsx';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import ActionAccordion from './actions-accordion.tsx';
import DecomAction from './decom-action.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { get } from 'react-hook-form';
import { FormSubSteps } from '../../../../../shared/shared-form-stepper.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import SharedGroupedMultiSelect from '../../../../../shared/shared-grouped-multi-select.tsx';
import { FormSectionHeader } from '../../../../../shared/form-section-header.tsx';
import ActionRunStepEmptyState from './actions-run-step-empty-state.tsx';
import ExpandCollapseAllButton from '../../../../../shared/buttons/expand-collapse-all-button.tsx';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

interface DayActionsStepProps {
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  getValues: UseFormGetValues<DecomPolicyFormValues>;
  actionTypesWithErrors: ActionErrorEntry[];
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  day: number;
  setActionSubSteps: Dispatch<SetStateAction<FormSubSteps[]>>;
}

export default function DayActionStep({
  control,
  watch,
  errors,
  clearErrors,
  setValue,
  getValues,
  trigger,
  actionTypesWithErrors,
  setActionTypesWithErrors,
  day,
  setActionSubSteps,
}: DayActionsStepProps) {
  const theme = useTheme();
  const sharedFormProps = {
    control,
    setValue,
    getValues,
    watch,
    errors,
    clearErrors,
    setActionTypesWithErrors,
    trigger,
    day,
  };

  const { currentTier, features } = useUserSettings();

  const actionsDataRaw = watch(`actions.${day}.actions`);
  const actionsData = useMemo(() => actionsDataRaw ?? [], [actionsDataRaw]);
  const defaultUserToNotify = watch('defaultUserToNotify') ?? [];
  const defaultNotifyUser = defaultUserToNotify.length > 0 ? true : false;
  const defaultUseManagerEmail = watch('defaultUseManagerEmail') ?? null;
  const defaultDestUser = watch('defaultDestUser') ?? undefined;

  const validateUserIsStillInDecomOrg =
    watch('validateUserIsStillInDecomOrg') || false;
  const validateUserIsStillSuspended =
    watch('validateUserIsStillSuspended') || false;

  const [expandedId, setExpandedId] = useState<string[]>([]);

  const selectedActionTypes = actionsData
    .map((a) => a.actionType)
    .filter(Boolean) as string[];

  const handleExpandCollapseAll = () => {
    if (expandedId.length === selectedActionTypes.length) setExpandedId([]);
    else setExpandedId(selectedActionTypes);
  };

  const handleToggleExpand = (id: string, isExpanding: boolean) => {
    setExpandedId((prev) =>
      isExpanding ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const availableOptions = decomActions.filter(
    (option) =>
      !(option.value === 'unsuspendUser' && validateUserIsStillSuspended) &&
      !(option.value === 'UserOrgChange' && validateUserIsStillInDecomOrg) &&
      !(isVaultExportAction(option.value) && !features?.vaultDecomAction) &&
      !(option.value === 'removeSlackChannels' && !features?.slackIntegration)
  );

  const allActions = watch('actions') ?? [];

  const selectedActionTypesInPreviousDays = Object.values(allActions)
    .filter(Boolean)
    .filter((dayData) => String(dayData?.day) !== String(day))
    .flatMap((dayData) =>
      (dayData?.actions || [])
        .map((action) => action?.actionType)
        .filter((actionType): actionType is string => Boolean(actionType))
    );

  const { fields, remove, replace } = useFieldArray({
    control,
    name: `actions.${day}.actions`,
  });

  const handleSelectAction = (selectedActionTypes: string[]) => {
    const existingForDay = getValues(`actions.${day}.actions`) ?? [];

    const retained = existingForDay.filter((action) =>
      selectedActionTypes.includes(action.actionType ?? '')
    );

    const newOnes = selectedActionTypes
      .filter(
        (actionType) =>
          !existingForDay.some((action) => action.actionType === actionType)
      )
      .map((actionType) =>
        createDefaultActionData(
          actionType,
          defaultNotifyUser,
          defaultUserToNotify,
          defaultUseManagerEmail,
          defaultDestUser ?? {
            userEmail: '',
            role: 'reader',
            type: 'user',
          },
          currentTier
        )
      );

    const updatedForDay = [...retained, ...newOnes];
    replace(updatedForDay);
    newOnes.forEach((action, idxOffset) => {
      const idx = retained.length + idxOffset;

      trigger(`actions.${day}.actions.${idx}`).then(() => {
        const fieldErrors = get(
          errors as FieldErrors,
          `actions.${day}.actions.${idx}`
        );

        if (fieldErrors) {
          setActionTypesWithErrors((prev) => {
            const alreadyExists = prev.some(
              (entry) => entry.id === action.actionType
            );

            if (!alreadyExists) {
              return [
                ...prev,
                {
                  id: action.actionType,
                  errors: fieldErrors,
                },
              ];
            }

            return prev;
          });
        } else {
          setActionTypesWithErrors((prev) =>
            prev.filter((entry) => entry.id !== action.actionType)
          );
        }

        clearErrors(`actions.${day}.actions.${idx}`);
      });
    });

    clearErrors(`actions.${day}`);
  };

  const handleRemoveAction = (actionTypeToRemove: string) => {
    const index = fields.findIndex(
      (action) => action.actionType === actionTypeToRemove
    );

    if (index === -1) return;

    remove(index);

    setActionTypesWithErrors((prev) =>
      prev.filter((entry) => entry.id !== actionTypeToRemove)
    );

    setActionSubSteps((prev) => {
      const stepIndexLabel =
        day === 0
          ? 'First Run Configuration'
          : day > 1
          ? `${day} Days Out`
          : `${day} Day Out`;

      return prev.filter(
        (value) =>
          !(
            value.id === actionTypeToRemove &&
            value.stepIndex === stepIndexLabel
          )
      );
    });

    setExpandedId((prev) => prev.filter((item) => item !== actionTypeToRemove));
  };

  // For stepper, to add selected actions and set warning if "Needs Attention"
  useEffect(() => {
    setActionSubSteps((prev) => {
      // 1. Filter out all existing actions for this specific day/stepIndex from previous state
      const stepIndexLabel =
        day === 0
          ? 'First Run Configuration'
          : day > 1
          ? `${day} Days Out`
          : `${day} Day Out`;

      const filteredPrev = prev.filter(
        (item) => item.stepIndex !== stepIndexLabel
      );

      // 2. Generate the fresh, current active actions for this day
      const currentDaySubsteps = (actionsData ?? []).map((x) => {
        const actionError = actionTypesWithErrors.find(
          (entry) => entry.id === x.actionType
        );

        const action = decomActions.find((opt) => opt.value === x.actionType);

        return {
          id: x.actionType,
          stepIndex: stepIndexLabel,
          title: action?.label || x.actionType || 'Unknown Action',
          hasError: !!actionError,
        };
      });

      // 3. Combine them to get the complete stepper state across all days
      return [...filteredPrev, ...currentDaySubsteps];
    });
  }, [actionTypesWithErrors, actionsData, day, setActionSubSteps]);

  return (
    <Grid container spacing={2} sx={{ p: 0, height: '100%' }}>
      <Grid
        size={{ md: 4.7 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <FormSectionHeader description="You can select actions by checking one or more actions on the list.">
          Action List
        </FormSectionHeader>
        <Box
          gap={1}
          sx={{
            width: '100%',
            alignItems: 'start',
            mt: 1,
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <Controller
            control={control}
            name={`actions.${day}.actions`}
            defaultValue={[]}
            render={() => (
              <SharedGroupedMultiSelect
                options={availableOptions}
                value={selectedActionTypes}
                onChange={handleSelectAction}
                disabledValues={selectedActionTypesInPreviousDays}
                checkboxTooltipLabel="This action is already selected in another day’s config"
              />
            )}
          />
        </Box>
      </Grid>
      <Grid
        size={{
          md: 7.3,
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FormSectionHeader description="You can remove actions by clicking the ‘Trash’ icon.">
            Selected Actions
          </FormSectionHeader>

          <ExpandCollapseAllButton
            onClick={handleExpandCollapseAll}
            expanded={expandedId.length === actionsData.length}
            disabled={selectedActionTypes.length === 0}
          />
        </Stack>
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.semantic.surface.bg2,
            flexGrow: 1,
            minHeight: 0,
            mt: 1,
            borderRadius: '4px',
            overflowY: 'auto',
          }}
        >
          {selectedActionTypes.length < 1 ? (
            <ActionRunStepEmptyState
              variant={errors.actions?.[day]?.actions ? 'error' : 'default'}
            />
          ) : (
            <Box
              gap={0.2}
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {selectedActionTypes
                .slice()
                .reverse()
                .map((actionTypeValue) => {
                  const action = decomActions.find(
                    (a) => a.value === actionTypeValue
                  );
                  if (!action) return null;
                  const { label, description, groupType } = action;
                  const actionType = actionTypeValue;

                  return (
                    <ActionAccordion
                      id={actionTypeValue}
                      key={actionTypeValue}
                      title={label}
                      groupType={groupType}
                      description={description}
                      expanded={expandedId.includes(actionType)}
                      onToggleExpand={(isExpanded) =>
                        handleToggleExpand(actionType, isExpanded)
                      }
                      onRemove={() => handleRemoveAction(actionTypeValue)}
                      {...sharedFormProps}
                      actionTypesWithErrors={actionTypesWithErrors}
                      day={day}
                    >
                      <DecomAction
                        {...sharedFormProps}
                        setActionTypesWithErrors={setActionTypesWithErrors}
                        actionType={actionTypeValue}
                        actionDisplayValue={label}
                      />
                    </ActionAccordion>
                  );
                })}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
